import { Plus, Trash2 } from "lucide-react";

const inp = "w-full rounded-xl border-2 border-slate-200 px-3.5 py-3 text-[16px] outline-none focus:border-slate-400";
export const Q = ({ label, hint, children }) => <div className="space-y-2"><div className="text-sm font-semibold text-slate-800">{label}</div>{hint && <p className="text-xs text-slate-500 -mt-1">{hint}</p>}{children}</div>;
export const Sec = ({ title, sub, testid, children }) => (
  <div className="space-y-4 border-t border-slate-100 pt-5" data-testid={testid}>
    <div><h3 className="font-heading text-lg text-ec-green">{title}</h3>{sub && <p className="text-sm text-slate-500 mt-0.5">{sub}</p>}</div>
    {children}
  </div>
);

export function YesNo({ value, onChange, testid }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {[[true, "Sim"], [false, "Não"]].map(([v, l]) => (
        <button key={l} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v ? "sim" : "nao"}`} aria-pressed={value === v}
          className={`py-3 rounded-xl border-2 text-[15px] font-semibold transition-colors ${value === v ? "bg-ec-green border-ec-green text-white" : "border-slate-200 text-slate-700 bg-white"}`}>{l}</button>
      ))}
    </div>
  );
}

const chip = (on) => `px-3.5 py-2.5 rounded-xl border-2 text-sm font-medium transition-colors ${on ? "bg-ec-green border-ec-green text-white" : "border-slate-200 text-slate-700 bg-white"}`;
export const Opts = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(([k, l]) => <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testid}-${k}`} aria-pressed={value === k} className={chip(value === k)}>{l}</button>)}
  </div>
);
const Multi = ({ options, value = [], onChange, testid }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(([k, l]) => <button key={k} type="button" onClick={() => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k])}
      data-testid={`${testid}-${k}`} aria-pressed={value.includes(k)} className={chip(value.includes(k))}>{l}</button>)}
  </div>
);

export const field = (obj, set, k, label, testid, extra = {}) => (
  <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">{label}</span>
    <input className={inp} value={obj[k] || ""} onChange={(e) => set({ ...obj, [k]: e.target.value })} data-testid={testid} {...extra} /></label>
);

// Identificação completa (secção A e, para o procurador, secção B).
export function IdentityFields({ v, set, prefix }) {
  const f = (k, l, extra) => field(v, set, k, l, `${prefix}-${k}`, extra);
  return (
    <>
      {f("full_name", "Nome completo")}
      <div className="grid grid-cols-2 gap-2">{f("birth_date", "Data de nascimento", { type: "date" })}{f("birth_place", "Local de nascimento")}</div>
      <div className="grid grid-cols-2 gap-2">{f("nationality", "Nacionalidade")}{f("nationality2", "Segunda nacionalidade (se tiver)")}</div>
      {f("address", "Morada de residência")}
      {f("fiscal_address", "Morada de domicílio fiscal (se for diferente)")}
      <div className="grid grid-cols-2 gap-2">{f("id_doc_type", "Documento (CC, passaporte…)")}{f("id_doc_number", "N.º do documento")}</div>
      <div className="grid grid-cols-2 gap-2">{f("id_doc_valid", "Validade do documento", { type: "date" })}{f("nif", "NIF", { inputMode: "numeric" })}</div>
      <div className="grid grid-cols-2 gap-2">{f("phone", "Telefone", { type: "tel" })}{f("email", "Email", { type: "email" })}</div>
      <div className="grid grid-cols-2 gap-2">{f("profession", "Profissão")}{f("employer", "Entidade patronal (se aplicável)")}</div>
    </>
  );
}

// D · Transação (só comprador).
export function DealKyc({ v, set }) {
  const o = (k) => (x) => set({ ...v, [k]: x });
  return (
    <Sec title="D · Transação" sub="De onde vem o dinheiro e como vai pagar." testid="kyc-deal">
      <Q label="Origem dos fundos"><Opts options={[["proprios", "Fundos próprios"], ["financiamento", "Financiamento"], ["misto", "Misto"]]} value={v.origin} onChange={o("origin")} testid="kyc-deal-origin" /></Q>
      <Q label="Proveniência">
        <Opts options={[["deposito", "Depósito bancário"], ["certificados", "Certificados de aforro / Tesouro"], ["venda_ativo", "Venda de um ativo"], ["outro", "Outro"]]} value={v.provenance} onChange={o("provenance")} testid="kyc-deal-provenance" />
        {v.provenance === "outro" && field(v, set, "provenance_other", "Qual?", "kyc-deal-provenance-other")}
      </Q>
      <Q label="Finalidade">
        <Opts options={[["habitacao", "Habitação"], ["arrendamento", "Arrendamento"], ["investimento", "Investimento"], ["outro", "Outro"]]} value={v.purpose} onChange={o("purpose")} testid="kyc-deal-purpose" />
        {v.purpose === "outro" && field(v, set, "purpose_other", "Qual?", "kyc-deal-purpose-other")}
      </Q>
      <Q label="Meios de pagamento" hint="Pode escolher vários. Vai ser pedido o comprovativo de que a conta é sua.">
        <Multi options={[["cheque", "Cheque"], ["numerario", "Numerário"], ["transferencia", "Transferência"], ["ativo", "Ativo"], ["outro", "Outro"]]} value={v.payment} onChange={o("payment")} testid="kyc-deal-payment" />
        {(v.payment || []).includes("outro") && field(v, set, "payment_other", "Qual?", "kyc-deal-payment-other")}
      </Q>
    </Sec>
  );
}

const PEP_Q = "Tem, ou teve nos últimos 12 meses, um cargo político ou público importante?";

// Empresa: dados da empresa, representante no ato e beneficiários efetivos (ou declaração do RCBE).
export function CompanyKyc({ v, set }) {
  const rep = v.rep || {};
  const owners = v.owners?.length ? v.owners : [{}];
  const setRep = (r) => set({ ...v, rep: r });
  const setOwner = (i, o) => set({ ...v, owners: owners.map((x, j) => (j === i ? o : x)) });
  return (
    <Sec title="A empresa" sub="Dados da empresa, de quem a representa e dos beneficiários efetivos." testid="kyc-company">
      {field(v, set, "name", "Denominação", "kyc-company-name")}
      {field(v, set, "nipc", "NIPC", "kyc-company-nipc", { inputMode: "numeric" })}
      {field(v, set, "seat", "Sede", "kyc-company-seat")}
      {field(v, set, "activity", "Objeto / atividade", "kyc-company-activity")}
      <Q label="Quem representa a empresa no ato">
        {field(rep, setRep, "name", "Nome do gerente ou administrador", "kyc-rep-name")}
        <Opts options={[["gerente", "Gerente"], ["administrador", "Administrador"]]} value={rep.role} onChange={(r) => setRep({ ...rep, role: r })} testid="kyc-rep-role" />
        <div className="grid grid-cols-2 gap-2">
          {field(rep, setRep, "doc_type", "Documento (ex.: CC)", "kyc-rep-doc-type")}
          {field(rep, setRep, "doc_number", "N.º do documento", "kyc-rep-doc-number")}
        </div>
      </Q>
      <Q label={`Representante: ${PEP_Q.toLowerCase()}`}><YesNo value={rep.pep} onChange={(a) => setRep({ ...rep, pep: a })} testid="kyc-rep-pep" /></Q>
      <label className="flex items-center gap-3 text-[15px] text-slate-800"><input type="checkbox" checked={!!v.bo_by_rcbe} onChange={(e) => set({ ...v, bo_by_rcbe: e.target.checked })} className="w-5 h-5 accent-[#12332B]" data-testid="kyc-bo-rcbe" /> Envio a declaração do RCBE nos documentos, em vez de escrever</label>
      {!v.bo_by_rcbe && owners.map((o, i) => (
        <div key={i} className="space-y-2 rounded-xl bg-slate-50 p-3" data-testid={`kyc-bo-${i}`}>
          <div className="flex items-center"><span className="mr-auto text-sm font-semibold text-slate-700">Beneficiário efetivo {i + 1}</span>
            {owners.length > 1 && <button type="button" onClick={() => set({ ...v, owners: owners.filter((_, j) => j !== i) })} className="text-slate-400" aria-label="Retirar" data-testid={`kyc-bo-remove-${i}`}><Trash2 size={16} /></button>}</div>
          {field(o, (x) => setOwner(i, x), "name", "Nome", `kyc-bo-name-${i}`)}
          <div className="grid grid-cols-2 gap-2">
            {field(o, (x) => setOwner(i, x), "birth_date", "Data de nascimento", `kyc-bo-birth-${i}`, { type: "date" })}
            {field(o, (x) => setOwner(i, x), "pct", "% participação", `kyc-bo-pct-${i}`)}
          </div>
          {field(o, (x) => setOwner(i, x), "nationality", "Nacionalidade", `kyc-bo-nat-${i}`)}
          <div className="text-xs text-slate-600">{PEP_Q}</div>
          <YesNo value={o.pep} onChange={(a) => setOwner(i, { ...o, pep: a })} testid={`kyc-bo-pep-${i}`} />
        </div>
      ))}
      {!v.bo_by_rcbe && owners.length < 10 && <button type="button" onClick={() => set({ ...v, owners: [...owners, {}] })} className="inline-flex items-center gap-1 text-sm font-medium text-ec-green" data-testid="kyc-bo-add"><Plus size={15} /> Outro beneficiário efetivo</button>}
    </Sec>
  );
}

// Estrangeiro / não residente.
export function ForeignKyc({ v, set, seller }) {
  return (
    <Sec title="Não residente / estrangeiro" sub="Residência fiscal e morada fora de Portugal." testid="kyc-foreign">
      {field(v, set, "tax_country", "País de residência fiscal", "kyc-foreign-country")}
      {field(v, set, "abroad_address", "Morada no estrangeiro", "kyc-foreign-address")}
      {seller && (
        <Q label="Representação fiscal" hint="Quem vive fora da UE/EEE precisa de representante fiscal ou de aderir às notificações eletrónicas da AT.">
          <Opts options={[["representante", "Tenho representante fiscal"], ["notificacoes", "Aderi às notificações eletrónicas"], ["nao_preciso", "Vivo na UE/EEE"]]}
            value={v.fiscal_rep} onChange={(x) => set({ ...v, fiscal_rep: x })} testid="kyc-foreign-fiscal" />
        </Q>
      )}
    </Sec>
  );
}

// B · Representação legal: identificação do procurador e a procuração.
export function ProxyKyc({ v, set }) {
  const poa = v.poa || {};
  const setPoa = (x) => set({ ...v, poa: x });
  return (
    <Sec title="B · Representação legal" sub="Dados do procurador. A procuração vai em anexo, nos documentos." testid="kyc-proxy">
      <IdentityFields v={v} set={set} prefix="kyc-b" />
      <Q label="A procuração">
        <div className="grid grid-cols-2 gap-2">
          {field(poa, setPoa, "date", "Data", "kyc-poa-date", { type: "date" })}
          {field(poa, setPoa, "issuer", "Entidade que a emitiu", "kyc-poa-issuer")}
        </div>
        <label className="flex items-center gap-3 text-[15px] text-slate-800"><input type="checkbox" checked={!!poa.foreign} onChange={(e) => setPoa({ ...poa, foreign: e.target.checked })} className="w-5 h-5 accent-[#12332B]" data-testid="kyc-poa-foreign" /> Foi feita fora de Portugal</label>
        {poa.foreign && <label className="flex items-center gap-3 text-[15px] text-slate-800"><input type="checkbox" checked={!!poa.apostille} onChange={(e) => setPoa({ ...poa, apostille: e.target.checked })} className="w-5 h-5 accent-[#12332B]" data-testid="kyc-poa-apostille" /> Tem apostila (ou está legalizada)</label>}
      </Q>
    </Sec>
  );
}
